import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Navbar from "./Navbar";
import ColorRingCustomLoader from "./Loader";
import { getCertificateById } from "../CertificateService";
import footer from "../../src/assets/img/mainfooterbg.png";
// import "./ApplicationDetails.css";

const API_BASE_URL = "https://api.wbpms.in";

const icons = {
    arrowLeft: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2Z" />
        </svg>
    ),
    location: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
        </svg>
    ),
    user: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-5.33 0-8 2.67-8 6v2h16v-2c0-3.33-2.67-6-8-6Z" />
        </svg>
    ),
    certificate: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 2h12l4 4v16H4V2Zm11 1.5V7h3.5L15 3.5ZM7 11h10V9H7v2Zm0 4h10v-2H7v2Zm0 4h7v-2H7v2Z" />
        </svg>
    ),
    document: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 2h9l5 5v15H6V2Zm8 1.5V8h4.5L14 3.5ZM9 12h8v-2H9v2Zm0 4h8v-2H9v2Zm0 4h5v-2H9v2Z" />
        </svg>
    ),
    external: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 3v2h3.59l-8.3 8.29 1.42 1.42L19 6.41V10h2V3h-7ZM5 5h6V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6h-2v6H5V5Z" />
        </svg>
    ),
    phone: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m6.62 10.79 2.2-2.2-3.4-3.4-2.2 2.2C1.8 8.81 3.42 13.35 7.1 17.03c3.68 3.68 8.22 5.3 9.64 3.88l2.2-2.2-3.4-3.4-2.2 2.2c-1.18-.39-2.62-1.32-3.92-2.62-1.3-1.3-2.23-2.74-2.8-4.1Z" />
        </svg>
    ),
};

function SectionCard({ icon, title, description, children, className = "" }) {
    return (
        <section className={`application-section-card ${className}`.trim()}>
            <div className="application-section-card__header">
                <span className="application-section-card__icon">{icon}</span>
                <div>
                    <h2>{title}</h2>
                    {description ? <p>{description}</p> : null}
                </div>
            </div>
            <div className="application-section-card__body">{children}</div>
        </section>
    );
}

function InfoItem({ label, value, wide = false, mono = false }) {
    const displayValue = value === null || value === undefined || value === "" ? "—" : value;

    return (
        <div className={`application-info-item ${wide ? "application-info-item--wide" : ""}`}>
            <span className="application-info-item__label">{label}</span>
            <span className={`application-info-item__value ${mono ? "application-info-item__value--mono" : ""}`}>
                {displayValue}
            </span>
        </div>
    );
}

function getFileUrl(path) {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

function DocumentCard({ index, type, number, path }) {
    const url = getFileUrl(path);

    return (
        <article className="application-document-card">
            <span className="application-document-card__index">{String(index).padStart(2, "0")}</span>
            <div className="application-document-card__content">
                <span className="application-document-card__eyebrow">Supporting document</span>
                <h3>{type || `Document ${index}`}</h3>
                <p>
                    Document number: <strong>{number || "Not provided"}</strong>
                </p>
            </div>
            {url ? (
                <a className="application-document-card__button" href={url} target="_blank" rel="noreferrer">
                    <span>View</span>
                    {icons.external}
                </a>
            ) : (
                <span className="application-document-card__missing">Unavailable</span>
            )}
        </article>
    );
}

function ApplicationDetails() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const applicationId = location.state?.applicationId || searchParams.get("id");

    const [certificateById, setCertificateById] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        let isMounted = true;

        async function loadApplication() {
            if (!applicationId) {
                setLoadError("Application ID is missing. Please open the application from the certificate list.");
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setLoadError("");
                const result = await getCertificateById(applicationId);

                if (isMounted) {
                    setCertificateById(result?.data || {});
                }
            } catch (error) {
                console.error("Unable to load application details:", error);
                if (isMounted) {
                    setLoadError("Unable to load application details. Please try again.");
                    toast.error("Unable to load application details");
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        }

        loadApplication();
        return () => {
            isMounted = false;
        };
    }, [applicationId]);

    const certificateTypes = useMemo(() => {
        if (!certificateById) return [];

        return [
            certificateById.CHAR_CERT,
            certificateById.INCM_CERT,
            certificateById.RESI_CERT,
            certificateById.SPER_CERT,
            certificateById.DIST_CERT,
            certificateById.CAST_CERT,
            certificateById.UNMARRIED_CERT,
        ]
            .filter((item) => item !== null && item !== undefined)
            .map((item) => String(item).trim())
            .filter((item) => item && item !== "-");
    }, [certificateById]);

    const hasIncomeCertificate = certificateTypes.some((item) =>
        item.toLowerCase().startsWith("income certificate")
    );
    const hasDistanceCertificate = certificateTypes.some((item) =>
        item.toLowerCase().startsWith("distance certificate")
    );
    const hasCasteCertificate = certificateTypes.some((item) =>
        item.toLowerCase().startsWith("caste certificate")
    );
    const hasSamePersonCertificate = certificateTypes.some((item) =>
        item.toLowerCase().startsWith("same person certificate")
    );

    const applicantPhotoUrl = getFileUrl(certificateById?.APPLICANT_IMG_LINK);
    const applicantInitial = certificateById?.NAME_APPLI?.trim()?.charAt(0)?.toUpperCase() || "A";

    if (isLoading) {
        return (
            <div className="application-page">
                <ToastContainer />
                <Navbar />
                <main className="application-loading-state">
                    <ColorRingCustomLoader />
                    <p>Loading application details…</p>
                </main>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="application-page">
                <ToastContainer />
                <Navbar />
                <main className="application-error-state">
                    <div className="application-error-state__icon">!</div>
                    <h1>Application details unavailable</h1>
                    <p>{loadError}</p>
                    <button type="button" onClick={() => navigate(-1)}>
                        {icons.arrowLeft}
                        Back to previous page
                    </button>
                </main>
            </div>
        );
    }

    return (
        <div className="application-page">
            <ToastContainer position="top-right" autoClose={3000} />
            <Navbar />

            <main className="application-shell">
                <button type="button" className="application-back-button" onClick={() => navigate(-1)}>
                    {icons.arrowLeft}
                    <span>Back to Previous page</span>
                </button>

                <header className="application-hero">
                    <div className="application-hero__glow application-hero__glow--one" />
                    <div className="application-hero__glow application-hero__glow--two" />

                    <div className="application-hero__content">
                        <span className="application-hero__eyebrow">Certificate application</span>
                        <h1>Application Details</h1>
                        <p>Review the applicant, Gram Panchayat, certificate and supporting-document information.</p>

                        <div className="application-hero__meta">
                            <div className="application-hero__id">
                                <span>Unique application ID</span>
                                <strong>{certificateById?.APPLICATION_ID || applicationId || "—"}</strong>
                            </div>
                            <div className="application-hero__stat">
                                <strong>{certificateTypes.length}</strong>
                                <span>Certificate{certificateTypes.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className="application-hero__stat">
                                <strong>{[certificateById?.link1, certificateById?.link2].filter(Boolean).length}</strong>
                                <span>Documents</span>
                            </div>
                        </div>
                    </div>

                    <div className="application-hero__profile">
                        <div className="application-hero__avatar">
                            {applicantPhotoUrl ? (
                                <img src={applicantPhotoUrl} alt={certificateById?.NAME_APPLI || "Applicant"} />
                            ) : (
                                <span>{applicantInitial}</span>
                            )}
                        </div>
                        <div>
                            <span>Applicant</span>
                            <strong>{certificateById?.NAME_APPLI || "Name unavailable"}</strong>
                            <small>{certificateById?.MOBILE_NO || "Mobile number unavailable"}</small>
                        </div>
                    </div>
                </header>

                <div className="application-content-grid">
                    <div className="application-content-grid__main">
                        <SectionCard
                            icon={icons.location}
                            title="Gram Panchayat Details"
                            description="Administrative and assessee information linked with this application."
                        >
                            <div className="application-info-grid">
                                <InfoItem label="District" value={certificateById?.DIST_NAME} />
                                <InfoItem label="Block" value={certificateById?.BLOCK_NAME} />
                                <InfoItem label="Gram Panchayat" value={certificateById?.GP_NAME} />
                                <InfoItem label="Sansad" value={certificateById?.SANSAD_NO_RM} />
                                <InfoItem label="Assessee number" value={certificateById?.ASSESSEE_NO} mono />
                                <InfoItem label="Assessee name" value={certificateById?.ASSESSEE_NAME} />
                                <InfoItem
                                    label="Relation with assessee"
                                    value={certificateById?.RELT_ASS}
                                    wide
                                />
                            </div>
                        </SectionCard>

                        <SectionCard
                            icon={icons.user}
                            title="Applicant Details"
                            description="Personal identity, contact and residential information."
                        >
                            <div className="application-info-grid">
                                <InfoItem label="Applicant name" value={certificateById?.NAME_APPLI} />
                                <InfoItem label="Gender" value={certificateById?.GENDER} />
                                <InfoItem label="Guardian name" value={certificateById?.GURDIAN_NM} />
                                <InfoItem label="Relation with guardian" value={certificateById?.REL_GURDIAN} />
                                <InfoItem label="Date of birth" value={certificateById?.DOB} />
                                <InfoItem label="PIN" value={certificateById?.PIN} mono />
                                <InfoItem label="Village" value={certificateById?.VILLAGE} />
                                <InfoItem label="Post office" value={certificateById?.POST_OFFICE} />
                                <InfoItem label="Police station" value={certificateById?.POLICE_STATION} />
                                <InfoItem label="Address" value={certificateById?.ADDRESS} wide />
                            </div>
                        </SectionCard>

                        {(hasIncomeCertificate ||
                            hasDistanceCertificate ||
                            hasCasteCertificate ||
                            hasSamePersonCertificate) && (
                                <SectionCard
                                    icon={icons.certificate}
                                    title="Certificate-specific Information"
                                    description="Additional information required for the selected certificate type."
                                >
                                    <div className="application-special-sections">
                                        {hasIncomeCertificate && (
                                            <div className="application-special-section">
                                                <h3>Income Certificate</h3>
                                                <div className="application-info-grid">
                                                    <InfoItem label="Source of income" value={certificateById?.INCM_SOURCE} />
                                                    <InfoItem
                                                        label="Annual family income"
                                                        value={certificateById?.INCM_ANNUAL}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {hasDistanceCertificate && (
                                            <div className="application-special-section">
                                                <h3>Distance Certificate</h3>
                                                <div className="application-info-grid">
                                                    <InfoItem
                                                        label="Distance from residence (KM)"
                                                        value={certificateById?.RESI_DISTANCE}
                                                    />
                                                    <InfoItem label="Location" value={certificateById?.RESI_LOCATION} />
                                                </div>
                                            </div>
                                        )}

                                        {hasCasteCertificate && (
                                            <div className="application-special-section">
                                                <h3>Caste Certificate</h3>
                                                <div className="application-info-grid application-info-grid--three">
                                                    <InfoItem label="Religion" value={certificateById?.RELIGION} />
                                                    <InfoItem label="Caste" value={certificateById?.CAST} />
                                                    <InfoItem label="Sub-caste" value={certificateById?.SUB_CAST} />
                                                </div>
                                            </div>
                                        )}

                                        {hasSamePersonCertificate && (
                                            <div className="application-special-section">
                                                <h3>Same Person Certificate</h3>
                                                <div className="application-info-grid">
                                                    <InfoItem label="Previous name" value={certificateById?.SPER_OLD_NAME} />
                                                    <InfoItem label="Current name" value={certificateById?.SPER_CURR_NAME} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </SectionCard>
                            )}

                        <SectionCard
                            icon={icons.document}
                            title="Supporting Documents"
                            description="Open the submitted documents in a new browser tab."
                        >
                            <div className="application-document-list">
                                <DocumentCard
                                    index={1}
                                    type={certificateById?.DOC_TYPE1}
                                    number={certificateById?.DOC_NO1}
                                    path={certificateById?.link1}
                                />
                                <DocumentCard
                                    index={2}
                                    type={certificateById?.DOC_TYPE2}
                                    number={certificateById?.DOC_NO2}
                                    path={certificateById?.link2}
                                />
                            </div>
                        </SectionCard>
                    </div>

                    <aside className="application-content-grid__aside">
                        <section className="application-side-card application-side-card--certificates">
                            <div className="application-side-card__header">
                                <span>{icons.certificate}</span>
                                <h2>Applied Certificates</h2>
                            </div>

                            {certificateTypes.length > 0 ? (
                                <div className="application-certificate-list">
                                    {certificateTypes.map((certificate, index) => (
                                        <div className="application-certificate-chip" key={`${certificate}-${index}`}>
                                            <span>{index + 1}</span>
                                            <strong>{certificate}</strong>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="application-side-card__empty">No certificate type is available.</p>
                            )}
                        </section>

                        <section className="application-side-card">
                            <div className="application-side-card__header">
                                <span>{icons.phone}</span>
                                <h2>Contact Details</h2>
                            </div>
                            <div className="application-contact-list">
                                <InfoItem label="Mobile number" value={certificateById?.MOBILE_NO} mono />
                                <InfoItem label="WhatsApp number" value={certificateById?.WHATSAPP_NO} mono />
                                <InfoItem label="Email address" value={certificateById?.EMAIL} />
                            </div>
                        </section>

                        <section className="application-side-card application-side-card--photo">
                            <div className="application-side-card__header">
                                <span>{icons.user}</span>
                                <h2>Applicant Photograph</h2>
                            </div>

                            <div className="application-photo-preview">
                                {applicantPhotoUrl ? (
                                    <img src={applicantPhotoUrl} alt={certificateById?.NAME_APPLI || "Applicant"} />
                                ) : (
                                    <div className="application-photo-preview__placeholder">
                                        <span>{applicantInitial}</span>
                                        <p>Photograph unavailable</p>
                                    </div>
                                )}
                            </div>

                            {applicantPhotoUrl ? (
                                <a href={applicantPhotoUrl} target="_blank" rel="noreferrer">
                                    View full photograph
                                    {icons.external}
                                </a>
                            ) : null}
                        </section>
                    </aside>

                </div>
                <br></br>
                <button type="button" className="application-back-button" onClick={() => navigate(-1)}>
                    {icons.arrowLeft}
                    <span>Back to Previous page</span>
                </button>
            </main>


            <footer className="application-footer">
                <img src={footer} alt="" aria-hidden="true" />
                <div className="application-footer__content">
                    <p>
                        © 2024 Designed and Developed by <strong>IT and Statistical Cell</strong>, Panchayat &amp;
                        Rural Development Department, <strong>Government of West Bengal</strong>
                    </p>
                    <small>Best viewed in a modern browser at 1920 × 1080 resolution or higher.</small>
                </div>
            </footer>
        </div>
    );
}

export default ApplicationDetails;
